import { useState } from "react";
import { Loader2, Star } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { formatApiError, submitReview } from "@/lib/api";
import { toast } from "@/components/ui/sonner";

const inputCls =
  "mt-1.5 w-full rounded-md border border-input bg-white/60 px-3.5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary transition";

export const ReviewFormDialog = ({ open, onOpenChange, product, orderNo, phone }) => {
  const [rating, setRating] = useState(5);
  const [title, setTitle] = useState("");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  if (!product) return null;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await submitReview({ product_slug: product.slug, order_no: orderNo, phone, rating, title, text });
      toast.success("Thank you! Your review is with us for approval.");
      setRating(5);
      setTitle("");
      setText("");
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="review-dialog" className="max-w-md bg-background">
        <DialogTitle className="font-serif text-2xl font-semibold tracking-tight">Rate {product.name}</DialogTitle>
        <p className="text-xs text-muted-foreground mt-1">Order {orderNo} · reviews appear after a quick check by our team</p>
        <form onSubmit={submit} className="mt-5 space-y-4">
          <div className="flex gap-1.5" data-testid="review-stars">
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                type="button"
                data-testid={`review-star-${n}`}
                onClick={() => setRating(n)}
                aria-label={`${n} star${n > 1 ? "s" : ""}`}
                className="p-1 transition-transform hover:scale-110"
              >
                <Star className={`w-7 h-7 ${n <= rating ? "fill-amber-400 text-amber-400" : "text-muted-foreground/40"}`} />
              </button>
            ))}
          </div>
          <label className="block">
            <span className="text-xs font-bold uppercase tracking-[0.1em] text-muted-foreground">Title (optional)</span>
            <input data-testid="review-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Great paper quality" className={inputCls} />
          </label>
          <label className="block">
            <span className="text-xs font-bold uppercase tracking-[0.1em] text-muted-foreground">Your review</span>
            <textarea data-testid="review-text" required rows={4} value={text} onChange={(e) => setText(e.target.value)} placeholder="How was the product?" className={`${inputCls} resize-none`} />
          </label>
          <button
            data-testid="review-submit"
            type="submit"
            disabled={busy}
            className="w-full rounded-full bg-primary text-primary-foreground py-3 text-sm font-bold flex items-center justify-center gap-2 hover:-translate-y-0.5 transition-transform disabled:opacity-60"
          >
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}
            {busy ? "Submitting…" : "Submit review"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
